import React, { useState, useEffect } from "react";
import UserSidebar from "../../components/UserSidebar";
import API from "../../services/api";
import { 
  FiStar, FiCheckCircle, FiClock, 
  FiRefreshCw, FiClipboard, FiX 
} from "react-icons/fi";

const EMOJI_MAP = {
  1: { emoji: "😡", label: "Very Bad" },
  2: { emoji: "😞", label: "Bad" },
  3: { emoji: "😐", label: "Okay" },
  4: { emoji: "😊", label: "Good" },
  5: { emoji: "😍", label: "Excellent" },
};

const UserReviews = () => {
  const [activeTab, setActiveTab] = useState("pending"); // "pending" | "completed"
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allTickets, setAllTickets] = useState([]);

  // Rating Modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ratingStars, setRatingStars] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    fetchReviewsData();
  }, []);

  const fetchReviewsData = async () => {
    setLoading(true);
    try {
      const [pendingRes, myTicketsRes] = await Promise.all([
        API.get("/tickets/pending-reviews").catch(() => ({ data: { success: false, data: [] } })),
        API.get("/tickets/my-tickets").catch(() => ({ data: { success: false, data: [] } })),
      ]);

      if (pendingRes.data?.success) {
        setPendingReviews(pendingRes.data.data || []);
      }
      if (myTicketsRes.data?.success) {
        setAllTickets(myTicketsRes.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  // Completed Reviews
  const completedReviews = allTickets.filter(t => t.rating_status === 1 || (t.rating && t.rating > 0));

  const openRatingModal = (ticket) => {
    setSelectedTicket(ticket);
    setRatingStars(0);
    setFeedback("");
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    if (!selectedTicket || ratingStars === 0) {
      alert("Please select at least 1 star to rate.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await API.post("/tickets/submit-rating", {
        ticketId: selectedTicket.id,
        rating: ratingStars,
        feedback: feedback.trim()
      });

      if (res.data?.success) {
        setToastMsg({ type: "success", text: "Thank you! Your feedback has been recorded." });
        setSelectedTicket(null);
        fetchReviewsData();
        setTimeout(() => setToastMsg(null), 4000);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit rating.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={s.layout}>
      <UserSidebar />

      <div style={s.main}>
        {/* HEADER */}
        <div style={s.pageHeader}>
          <div>
            <div style={s.breadcrumb}>SERVICE PORTAL &bull; QUALITY AUDIT</div>
            <h1 style={s.pageTitle}>Service Reviews & Ratings</h1>
            <p style={s.pageSubtitle}>
              Provide feedback for resolved service requests and view your verified rating history.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button 
              style={s.refreshBtn} 
              onClick={fetchReviewsData} 
              title="Refresh reviews"
            >
              <FiRefreshCw className={loading ? "spin" : ""} size={16} />
            </button>
          </div>
        </div>

        {/* TOAST MESSAGE */}
        {toastMsg && (
          <div style={{
            ...s.toastCard,
            background: toastMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
            color: toastMsg.type === "success" ? "#10B981" : "#EF4444",
            border: `1px solid ${toastMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`
          }}>
            <FiCheckCircle size={18} />
            <span>{toastMsg.text}</span>
          </div>
        )}

        {/* TABS */}
        <div style={s.tabBar}>
          <button
            onClick={() => setActiveTab("pending")}
            style={{
              ...s.tabBtn,
              ...(activeTab === "pending" ? s.tabBtnActive : {})
            }}
          >
            <FiClock size={15} />
            <span>Pending Reviews</span>
            <span style={{
              ...s.tabBadge,
              background: activeTab === "pending" ? "#D4AF37" : "#E2E8F0",
              color: activeTab === "pending" ? "#1B2559" : "#64748B"
            }}>
              {pendingReviews.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("completed")}
            style={{
              ...s.tabBtn,
              ...(activeTab === "completed" ? s.tabBtnActive : {})
            }}
          >
            <FiStar size={15} />
            <span>Submitted Ratings</span>
            <span style={{
              ...s.tabBadge,
              background: activeTab === "completed" ? "#D4AF37" : "#E2E8F0",
              color: activeTab === "completed" ? "#1B2559" : "#64748B"
            }}>
              {completedReviews.length}
            </span>
          </button>
        </div>

        {/* CONTENT */}
        {loading ? (
          <div style={s.centerBox}>
            <div style={s.spinner}></div>
            <div style={s.loadingText}>Loading review items...</div>
          </div>
        ) : activeTab === "pending" ? (
          pendingReviews.length === 0 ? (
            <div style={s.emptyCard}>
              <div style={s.emptyIconWrap}>
                <FiCheckCircle size={36} color="#10B981" />
              </div>
              <h3 style={s.emptyTitle}>All Caught Up!</h3>
              <p style={s.emptySubtitle}>You don't have any pending resolved tickets waiting for rating.</p>
            </div>
          ) : (
            <div style={s.grid}>
              {pendingReviews.map(t => (
                <div key={t.id} style={s.reviewCard}>
                  <div style={s.cardTop}>
                    <div>
                      <span style={s.goldCode}>{t.ticket_code}</span>
                      <div style={s.dateText}>
                        Resolved on {t.resolved_at ? new Date(t.resolved_at).toLocaleDateString("en-IN") : new Date(t.created_at).toLocaleDateString("en-IN")}
                      </div>
                    </div>
                    <span style={s.pendingBadge}>Awaiting Rating</span>
                  </div>

                  <div style={s.descWrap}>
                    <div style={s.descLabel}>Resolved Issue Description:</div>
                    <div style={s.descBody}>{t.description}</div>
                  </div>

                  <button style={s.rateActionBtn} onClick={() => openRatingModal(t)}>
                    <FiStar size={15} />
                    <span>Rate Experience</span>
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          completedReviews.length === 0 ? (
            <div style={s.emptyCard}>
              <div style={s.emptyIconWrap}>
                <FiClipboard size={36} color="#A3AED0" />
              </div>
              <h3 style={s.emptyTitle}>No Ratings Submitted Yet</h3>
              <p style={s.emptySubtitle}>Ratings submitted for resolved tickets will appear here.</p>
            </div>
          ) : (
            <div style={s.grid}>
              {completedReviews.map(t => (
                <div key={t.id} style={s.reviewCard}>
                  <div style={s.cardTop}>
                    <div>
                      <span style={s.goldCode}>{t.ticket_code}</span>
                      <div style={s.dateText}>
                        {new Date(t.created_at).toLocaleDateString("en-IN")}
                      </div>
                    </div>
                    <div style={s.verifiedPill}>
                      <FiCheckCircle size={12} /> Verified Rating
                    </div>
                  </div>

                  <div style={s.ratingStarsRow}>
                    {[1, 2, 3, 4, 5].map(st => (
                      <FiStar
                        key={st}
                        size={17}
                        fill={st <= (t.rating || 0) ? "#D4AF37" : "none"}
                        color={st <= (t.rating || 0) ? "#D4AF37" : "#CBD5E1"}
                      />
                    ))}
                    <span style={s.starScore}>{t.rating || 5}.0</span>
                  </div>

                  <div style={s.feedbackQuote}>
                    "{t.feedback || "Good and fast resolution!"}"
                  </div>

                  <div style={s.ticketFooter}>
                    <span>Dept: <strong>{t.category_name || "General"}</strong></span>
                    {t.worker_name && <span>Worker: <strong>{t.worker_name}</strong></span>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* RATING MODAL (MOBILE PARITY BOTTOM SHEET / POPUP) */}
        {selectedTicket && (
          <div style={s.modalOverlay} onClick={() => setSelectedTicket(null)}>
            <div style={s.modalContent} onClick={e => e.stopPropagation()}>
              <div style={s.modalHandle}></div>

              <div style={s.modalHeader}>
                <h3 style={s.modalTitle}>Rate Your Experience</h3>
                <button style={s.modalClose} onClick={() => setSelectedTicket(null)}>
                  <FiX size={16} />
                </button>
              </div>

              {/* TICKET DETAILS BOX (MOBILE STYLE) */}
              <div style={s.ticketDetailBox}>
                <div style={s.ticketDetailRow}>
                  <span style={s.ticketDetailLabel}>Ticket ID:</span>
                  <span style={s.ticketDetailVal}>{selectedTicket.ticket_code}</span>
                </div>
                <div style={s.ticketDivider}></div>
                <div style={s.ticketDetailRow}>
                  <span style={s.ticketDetailLabel}>Issue:</span>
                  <span style={s.ticketDetailDesc}>{selectedTicket.description}</span>
                </div>
              </div>

              {/* INTERACTIVE 5 STARS */}
              <div style={s.starsContainer}>
                {[1, 2, 3, 4, 5].map(index => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setRatingStars(index)}
                    style={{
                      ...s.starBtn,
                      transform: ratingStars === index ? "scale(1.2)" : "scale(1.0)"
                    }}
                  >
                    <FiStar
                      size={36}
                      fill={ratingStars >= index ? "#D4AF37" : "none"}
                      color={ratingStars >= index ? "#D4AF37" : "#CBD5E1"}
                    />
                  </button>
                ))}
              </div>

              {/* EMOJI & FEELING TEXT */}
              <div style={s.emojiWrap}>
                {ratingStars === 0 ? (
                  <span style={s.tapHint}>Tap a star to rate</span>
                ) : (
                  <div style={s.emojiDisplay}>
                    <div style={s.emojiIcon}>{EMOJI_MAP[ratingStars].emoji}</div>
                    <div style={s.emojiText}>{EMOJI_MAP[ratingStars].label}</div>
                  </div>
                )}
              </div>

              {/* FEEDBACK TEXTAREA */}
              <form onSubmit={handleSubmitRating} style={{ marginTop: 20 }}>
                <div style={{ marginBottom: 20 }}>
                  <textarea
                    rows="3"
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    placeholder="Write your feedback..."
                    style={s.feedbackInput}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || ratingStars === 0}
                  style={{
                    ...s.submitBtn,
                    opacity: ratingStars === 0 || submitting ? 0.6 : 1,
                    cursor: ratingStars === 0 || submitting ? "not-allowed" : "pointer"
                  }}
                >
                  {submitting ? "SUBMITTING..." : "SUBMIT FEEDBACK"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── STYLES ─────────────────────────────────────────────── */
const s = {
  layout: { display: "flex", background: "#F8FAFC", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif" },
  main: { marginLeft: 280, padding: "32px 36px 100px", flex: 1, minWidth: 0 },

  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 20 },
  breadcrumb: { fontSize: 11, color: "#D4AF37", fontWeight: 800, letterSpacing: 0.8, marginBottom: 6 },
  pageTitle: { margin: 0, fontSize: 26, fontWeight: 900, color: "#1B2559", letterSpacing: "-0.5px" },
  pageSubtitle: { margin: "4px 0 0", fontSize: 13, color: "#A3AED0", fontWeight: 500 },

  refreshBtn: { width: 42, height: 42, borderRadius: 12, border: "1px solid #E9EDF7", background: "#FFF", color: "#64748B", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },

  toastCard: { display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderRadius: 14, marginBottom: 20, fontSize: 13, fontWeight: 700 },

  tabBar: { display: "flex", gap: 8, borderBottom: "1.5px solid #F0F2FA", marginBottom: 28 },
  tabBtn: { display: "flex", alignItems: "center", gap: 8, padding: "12px 24px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 800, color: "#A3AED0", borderBottom: "3px solid transparent", fontFamily: "inherit" },
  tabBtnActive: { color: "#D4AF37", borderBottomColor: "#D4AF37" },
  tabBadge: { padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 800 },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 20 },
  reviewCard: { background: "#FFFFFF", borderRadius: 20, border: "1.5px solid #F0F2FA", padding: "22px 24px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", justifyContent: "space-between" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  goldCode: { fontSize: 15, fontWeight: 900, color: "#1B2559", letterSpacing: 0.4 },
  dateText: { fontSize: 11, color: "#94A3B8", marginTop: 2 },
  pendingBadge: { padding: "4px 10px", borderRadius: 99, background: "#FFFBEB", color: "#D97706", border: "1px solid #FDE68A", fontSize: 11, fontWeight: 800 },
  verifiedPill: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 99, background: "#ECFDF5", color: "#10B981", border: "1px solid #A7F3D0", fontSize: 11, fontWeight: 800 },

  descWrap: { background: "#F8FAFC", borderRadius: 12, padding: "12px 14px", border: "1px solid #F0F2FA", marginBottom: 16 },
  descLabel: { fontSize: 10, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
  descBody: { fontSize: 12, color: "#1B2559", lineHeight: 1.5 },

  rateActionBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "#1B2559", color: "#D4AF37", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 6px 18px rgba(27,37,89,0.12)" },

  ratingStarsRow: { display: "flex", alignItems: "center", gap: 5, marginBottom: 10 },
  starScore: { marginLeft: 6, fontSize: 13, fontWeight: 800, color: "#1B2559" },
  feedbackQuote: { fontSize: 13, fontStyle: "italic", color: "#475569", lineHeight: 1.5, marginBottom: 14 },
  ticketFooter: { borderTop: "1px dashed #E2E8F0", paddingTop: 10, display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94A3B8" },

  emptyCard: { background: "#FFF", borderRadius: 20, border: "1.5px solid #F0F2FA", padding: "80px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" },
  emptyIconWrap: { width: 68, height: 68, borderRadius: "50%", background: "#F8FAFC", border: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 },
  emptyTitle: { margin: "0 0 6px", fontSize: 17, fontWeight: 900, color: "#1B2559" },
  emptySubtitle: { margin: 0, fontSize: 13, color: "#94A3B8" },

  centerBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "100px 20px", gap: 12 },
  spinner: { width: 36, height: 36, border: "3px solid #E2E8F0", borderTopColor: "#1B2559", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loadingText: { fontSize: 13, color: "#A3AED0", fontWeight: 600 },

  /* MODAL */
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modalContent: { width: "min(460px, 100%)", background: "#FFF", borderRadius: 24, boxShadow: "0 24px 60px rgba(0,0,0,0.2)", padding: "26px 28px", boxSizing: "border-box" },
  modalHandle: { width: 44, height: 4, background: "#CBD5E1", borderRadius: 10, margin: "0 auto 16px" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
  modalTitle: { margin: 0, fontSize: 19, fontWeight: 900, color: "#1B2559" },
  modalClose: { background: "none", border: "none", color: "#94A3B8", cursor: "pointer", padding: 4 },

  ticketDetailBox: { background: "#FFFDF5", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 16, padding: "14px 16px", marginBottom: 20 },
  ticketDetailRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  ticketDetailLabel: { fontSize: 12, color: "#64748B", fontWeight: 600 },
  ticketDetailVal: { fontSize: 13, fontWeight: 900, color: "#D4AF37" },
  ticketDivider: { height: 1, background: "rgba(212,175,55,0.15)", margin: "8px 0" },
  ticketDetailDesc: { fontSize: 12, fontWeight: 600, color: "#1B2559", maxWidth: "70%", textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  starsContainer: { display: "flex", justifyContent: "center", gap: 10, margin: "14px 0" },
  starBtn: { background: "none", border: "none", cursor: "pointer", padding: 4, transition: "transform 0.15s" },

  emojiWrap: { textAlign: "center", minHeight: 60, display: "flex", alignItems: "center", justifyContent: "center" },
  tapHint: { fontSize: 13, color: "#94A3B8", fontWeight: 600 },
  emojiDisplay: { display: "flex", flexDirection: "column", alignItems: "center" },
  emojiIcon: { fontSize: 36, lineHeight: 1 },
  emojiText: { fontSize: 14, fontWeight: 800, color: "#D4AF37", marginTop: 4 },

  feedbackInput: { width: "100%", padding: "12px 14px", borderRadius: 14, border: "1.5px solid #F0F2FA", background: "#F8FAFC", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  submitBtn: { width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "#D4AF37", color: "#1B2559", fontSize: 13, fontWeight: 900, letterSpacing: 0.8, boxShadow: "0 6px 18px rgba(212,175,55,0.25)" },
};

export default UserReviews;
